import { useState, useCallback, useEffect } from 'react'
import {
  TextField,
  IconButton,
  SvgIcon,
  SxProps,
  debounce as debounceFn,
} from '@applift/factor'
import { Search as SearchIcon, Close } from '@applift/icons'

interface SearchProps {
  sx?: SxProps
  search: string
  setSearch: (searchStr: string) => void
  placeholder?: string
  debounce?: number
  fullWidth?: boolean
}

export const Search = (props: SearchProps) => {
  const { sx, search, setSearch, placeholder, debounce, fullWidth } = props
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [localValue, setLocalValue] = useState(search)

  useEffect(() => {
    if (search !== localValue) {
      setLocalValue(search)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnSearch = useCallback(
    debounceFn((e: string) => {
      setSearch(e)
    }, debounce || 0),
    [debounce, setSearch]
  )

  const onSearchChange = useCallback(
    (e: any) => {
      setLocalValue(e.target.value)
      if (debounce) {
        debouncedOnSearch(e.target.value)
      } else {
        setSearch(e.target.value)
      }
    },
    [debouncedOnSearch, setSearch, debounce]
  )

  const onSearchFocus = () => {
    setIsSearchFocused(true)
  }

  const onSearchBlur = () => {
    setIsSearchFocused(false)
  }

  return (
    <TextField
      type="text"
      sx={sx}
      placeholder={placeholder}
      variant="outlinedDash"
      color="primary"
      onFocus={onSearchFocus}
      onBlur={onSearchBlur}
      onChange={onSearchChange}
      value={localValue}
      fullWidth={fullWidth}
      InputProps={{
        startAdornment: (
          <SvgIcon
            size="medium"
            color={isSearchFocused ? 'primary' : 'secondary'}
          >
            <SearchIcon />
          </SvgIcon>
        ),
        endAdornment:
          search.length > 0 ? (
            <IconButton
              onClick={() => {
                setSearch('')
              }}
            >
              <Close fontSize={16} />
            </IconButton>
          ) : (
            <></>
          ),
      }}
    />
  )
}
